import { PERMISSIONS_PATH } from '@/path';
import { Link, List, ListItem, Typography, TypographyProps } from '@mui/material';
import { PrivacyPolicyLink, TermsOfUseLink } from '@verifime/components';
import tokens from '@verifime/design-tokens';

export const NO_SELECTED_PERMISSION = 'Please select an organisation to grant the permission';
export const NO_ORGANISATION = 'There is no any organisation';
export const NOT_SEARCHED = 'Please search to find organisation';
export const NO_ACTIVE_PERMISSIONS = 'No active permissions';
export const FAILED_TO_LOAD_IDENTITIES = 'Failed to load identities';
export const FORM_VALIDATION_FAILED =
  'There are validation errors in the form, please correct the data.';
// DRAFT copy, not yet approved: proposed for #616, Charles approves or replaces it.
export const BACK_CLOSED_AFTER_ENTITY_DRAFT =
  'You have already started this identity, so you can no longer go back to change your choice.';

// notification messages
export const IDENTITY_SUBMISSION_SUCCESS =
  'Your identity record has been submitted for verification.';
export const WHOLESALE_CERTIFICATE_SUBMISSION_SUCCESS =
  'Your Wholesale Certificate has been submitted for verification.';
export const PERMISSION_GRANTED_SUCCESS = 'Your permission record has been submitted.';
export const PERMISSION_REVOKED_SUCCESS =
  'Your permission cancellation request has been submitted.';

export const ACTIVE_PERMISSION_CANCEL_CONFIRM = 'Are you sure you wish to cancel this permission?';

export const DEFAULT_CONSENT =
  'I consent to my personal information provided above being checked with the documents issues or the official record holder, credit bureaus, mobile provider or other third-party systems via an identity service provider, for the purposes of identity verification and AML/CFT compliance. I consent for this information and results being stored for audit and compliance purposes.';

export const WIZARD_STEP1_NO_NEED_DISCARD = 'There is nothing needs to be discared.';
export const WIZARD_STEP1_DISCARD_PROMPT_TITLE = 'Discard draft';
export const WIZARD_STEP1_DISCARD_PROMPT_TEXT =
  'Are you sure you want to discard all the details you have entered for this identity?';
export const WIZARD_STEP1_DISCARD_BUTTON_CANCEL = 'Cancel';
export const WIZARD_STEP1_DISCARD_BUTTON_CONFIRM = 'Discard';
export const WIZARD_STEP1_DISCARD_SUCCESS = 'Discarded';

export const WIZARD_ORGANISATION_STEP5_SUCCESS_MESSAGES = {
  header: 'What happens next?',
  body: [
    'We will email you when the verification is complete.',
    'We will also inform all other individuals you have added as part of this organisation. They can join VerifiMe and keep track of how their details are used. They can also add any additional documents directly to VerifiMe.',
    <>
      Once verified, you can share your verified status with your service provider by granting them
      a <Link href={PERMISSIONS_PATH}>Permission</Link>.
    </>,
  ],
};

export const WIZARD_INDIVIDUAL_STEP5_SUCCESS_MESSAGES = {
  header: 'What happens next?',
  body: [
    'We will review your details to complete the verification process. If more information is needed we will contact you directly.',
    'We will notify you when the verification is complete.',
    <>
      Once verified, you can share your verified status with your service provider by granting them
      a <Link href={PERMISSIONS_PATH}>Permission</Link>.
    </>,
  ],
};

export const WIZARD_STEPS_TERMS_AND_CONDITIONS = (
  {
    variant = 'body1',
  }: {
    variant?: TypographyProps['variant'];
  } = { variant: 'body1' },
) => (
  <>
    <Typography variant={variant}>By clicking submit, I acknowledge, agree and confirm:</Typography>
    <List sx={{ listStyle: 'disc', pl: 4 }}>
      <ListItem sx={{ display: 'list-item', paddingLeft: tokens.spacingXs }}>
        that my information may be used for verification, including by being checked with relevant
        Government agencies and other, commercially operated, third-party systems for identity
        verification;
      </ListItem>
      <ListItem sx={{ display: 'list-item', paddingLeft: tokens.spacingXs }}>
        that the information provided for my verified state is true, accurate, and up to date and
        that service providers may need to access this information as part of their due diligence
        process.
      </ListItem>
      <ListItem sx={{ display: 'list-item', paddingLeft: tokens.spacingXs }}>
        that VerifiMe will store the details and results of this verification.
      </ListItem>
      <ListItem sx={{ display: 'list-item', paddingLeft: tokens.spacingXs }}>
        I have read and agree to be bound by VerifiMe&apos;s <TermsOfUseLink /> and{' '}
        <PrivacyPolicyLink />
      </ListItem>
    </List>
  </>
);

export const WIZARD_ORGANISATION_STEPS_TERMS_AND_CONDITIONS = (
  <>
    <Typography variant="body1">
      By clicking submit, I agree that my personal and contact information may be used for
      verification, including by being checked with relevant Government agencies and other,
      commercially operated, third-party systems for identity verification.
    </Typography>
    <Typography variant="body1">
      I acknowledge that VerifiMe will store the details and results of this verification.
    </Typography>
    <Typography variant="body1">
      I confirm the uploaded documents are current, and no subsequent versions or amendments exist.
    </Typography>
    <Typography variant="body1">
      When adding individuals to an organisation, that person will be notified by email and given
      the option to manage their identity status through VerifiMe directly.
    </Typography>
    <Typography variant="body1">
      I have read and agree to be bound by VerifiMe&apos;s <TermsOfUseLink /> and{' '}
      <PrivacyPolicyLink />.
    </Typography>
  </>
);

export const UPLOAD_SUPPORTING_DOCUMENTS = {
  title: 'Upload Supporting Documents',
  subtitle:
    'Add any documents that will assist in verifying this organisation (e.g., ASIC extract, constitution, shareholder agreement, dealer licence, etc.)',
};

export const TRUST_UPLOAD_SUPPORTING_DOCUMENTS = {
  title: 'Upload Supporting Documents',
  subtitle:
    'Add any documents that will assist in verifying this organisation (e.g., ASIC extract, Trust Deed, constitution, shareholder agreement, dealer licence, etc.)',
};

export const AT_LEAST_ONE_TRUSTEE = 'At least one trustee must be specified';
export const AT_LEAST_ONE_DIRECTOR = 'At least one director must be specified';
export const AT_LEAST_ONE_TRUST_DEED = 'At least one trust deed must be specified';
export const AT_LEAST_ONE_PARTNERSHIP_AGREEMENT =
  'At least one partnership agreement must be specified';
export const AT_LEAST_TWO_PARTNERS = 'At least two partners must be specified';

export const LANDING_TITLE = 'Stop sharing your IDs over and over';
export const LANDING_DESCRIPTION =
  'Build a VerifiMe identity once and only share your verified status with the service provider next time.';
export const META_DESCRIPTION =
  'VerifiMe is an identity verification platform for individuals, trusts and businesses.';

// client-side error boundary copy
export const ERROR_HEADLINE = 'Something interrupted this page';
export const ERROR_BODY_TRANSIENT =
  'This is on our side, not anything you did, and your details are safe. Try again, and if it keeps happening we can pick it up from here.';
export const ERROR_BODY_RECURRING =
  'This is on our side, not anything you did, and your details are safe. This page keeps running into the same problem, so the quickest way forward is to talk to us.';
export const ERROR_SECURITY_NOTE = 'Your information stays encrypted and secure';
export const ERROR_NOTIFIED = 'Our team has been automatically notified and is already looking into it.';
export const ERROR_LOOP_HINT =
  'That did not clear it. This page is likely hitting the same problem each time, so contacting our team is the most reliable way to get unstuck.';
export const ERROR_ACTION_TRY_AGAIN = 'TRY AGAIN';
export const ERROR_ACTION_CONTACT_SUPPORT = 'CONTACT SUPPORT';
export const ERROR_ACTION_BACK_TO_SIGN_IN = 'BACK TO SIGN IN';
export const SUPPORT_EMAIL = 'support@verifime.com';
